import { ApiClientsError } from '#pplx/frontend/api-clients/core/errors.js';
import { isAppNotificationsInfiniteData } from '#pplx/frontend/apps/ask/layout/sidebar/core/shared/appNotificationsData.js';
import { getAppNotificationsQueryKey, getUnreadNotificationCountQueryKey, } from '#pplx/frontend/apps/ask/queries/query-keys/notificationKeys.js';
import { notificationsClient } from '#pplx_generated/typespec/rest/notifications/client.js';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
export const cancelOptimisticNotificationCacheQueries = (queryClient, queryKeys) => Promise.all(queryKeys.map((queryKey) => queryClient.cancelQueries({
    exact: true,
    queryKey,
})));
export const invalidateNotificationLists = (queryClient) => Promise.all([
    queryClient.invalidateQueries({
        exact: true,
        queryKey: getAppNotificationsQueryKey(),
    }),
    queryClient.invalidateQueries({
        exact: true,
        queryKey: getAppNotificationsQueryKey(true),
    }),
]);
const updateNotificationsCache = (data, update) => {
    if (!isAppNotificationsInfiniteData(data)) {
        return data;
    }
    return {
        ...data,
        pages: data.pages.map((page) => ({
            ...page,
            notifications: update(page.notifications),
        })),
    };
};
const getCachedNotification = (data, notificationId) => {
    if (!isAppNotificationsInfiniteData(data)) {
        return;
    }
    return data.pages
        .flatMap((page) => page.notifications)
        .find((notification) => notification.notification_id === notificationId);
};
const decrementUnreadCount = (queryClient) => {
    queryClient.setQueryData(getUnreadNotificationCountQueryKey(), (count) => count === undefined ? count : Math.max(count - 1, 0));
};
export function useAppNotificationMutations({ reason }) {
    const queryClient = useQueryClient();
    const markAllAsRead = useMutation({
        mutationFn: async (variables) => {
            const { error, response } = await notificationsClient.POST('/rest/notifications/in-app/bulk-read', reason, {
                body: {
                    notification_ids: variables?.notificationIds,
                },
            });
            if (error) {
                throw new ApiClientsError('API_CLIENTS_ERROR', {
                    message: 'Failed to mark notifications as read',
                    cause: error,
                    status: response.status ?? 0,
                });
            }
        },
        onSuccess: () => {
            void invalidateNotificationLists(queryClient);
            void queryClient.invalidateQueries({
                queryKey: getUnreadNotificationCountQueryKey(),
            });
        },
    });
    const markAsRead = useMutation({
        mutationFn: async (variables) => {
            const { error, response } = await notificationsClient.POST(`/rest/notifications/in-app/{notification_id}/read`, reason, {
                params: {
                    path: {
                        notification_id: variables.notificationId,
                    },
                },
            });
            if (error) {
                throw new ApiClientsError('API_CLIENTS_ERROR', {
                    message: 'Failed to mark notification as read',
                    cause: error,
                    status: response.status ?? 0,
                });
            }
        },
        onMutate: async ({ notificationCenterEligible, notificationId, wasUnread }) => {
            await cancelOptimisticNotificationCacheQueries(queryClient, [
                getAppNotificationsQueryKey(),
                getAppNotificationsQueryKey(true),
                getUnreadNotificationCountQueryKey(),
            ]);
            const cachedNotification = getCachedNotification(queryClient.getQueryData(getAppNotificationsQueryKey()), notificationId) ??
                getCachedNotification(queryClient.getQueryData(getAppNotificationsQueryKey(true)), notificationId);
            const isNotificationCenterEligible = cachedNotification?.notification_center_enabled ?? notificationCenterEligible ?? true;
            const shouldDecrementUnreadCount = isNotificationCenterEligible &&
                (cachedNotification ? !cachedNotification.is_read : wasUnread === true);
            if (shouldDecrementUnreadCount) {
                decrementUnreadCount(queryClient);
            }
            queryClient.setQueryData(getAppNotificationsQueryKey(), (data) => updateNotificationsCache(data, (notifications) => {
                if (!isNotificationCenterEligible) {
                    return notifications.filter((notification) => notification.notification_id !== notificationId);
                }
                return notifications.map((notification) => notification.notification_id === notificationId
                    ? { ...notification, is_read: true }
                    : notification);
            }));
            queryClient.setQueryData(getAppNotificationsQueryKey(true), (data) => updateNotificationsCache(data, (notifications) => notifications.filter((notification) => notification.notification_id !== notificationId)));
        },
        onError: () => {
            void queryClient.invalidateQueries({
                queryKey: getUnreadNotificationCountQueryKey(),
            });
            void invalidateNotificationLists(queryClient);
        },
    });
    return useMemo(() => ({
        markAllAsRead,
        markAsRead,
    }), [markAllAsRead, markAsRead]);
}
//# sourceMappingURL=useAppNotificationMutations.js.map